// Claude im Homelab (docs/claude.md): Typen und reine Rechnung. Ohne
// Svelte, damit sie testbar ist (lib/claude.test.mjs).
//
// Die Brücke speichert je Chat die Zeilen von Claude Code unverändert
// (stream-json) und dazu eigene Ereignisse (Nachricht, Entscheidung,
// Zustand). Hier wird daraus, was der Chat zeigt: Nachrichten, Werkzeuge
// als kurze Zeilen (Ergebnis im Detail), Entscheidungen.

import type { LageTeil } from "../lage";
import type { Laeuft } from "../laeuft";

export type Zustand = "ruht" | "arbeitet" | "wartet" | "fehler";

/** Eine offene Rückfrage, wie sie der freie Stand nennt (GET /v2-api/claude,
 *  WS-Kanal „claude"): dass sie da ist, welches Werkzeug, seit wann. Ohne
 *  Inhalt -- Sicherheits-Nachprüfung 21.10.2026, Befund 2. */
export interface RueckfrageKurz {
  id: string;
  werkzeug: string;
  seit: string;
}

/** Die ganze Rückfrage: der Befehl, die Datei, die Fragen an dich. Nur über
 *  den Live-Socket des Chats (hinter der PIN, Ereignis „offen"). */
export interface Rueckfrage extends RueckfrageKurz {
  eingabe: Record<string, unknown> | null;
  text?: string;
  vorschlag?: unknown;
}

/** Was ein Chat bei einem Modell verbraucht hat (internal/claude
 *  chatverbrauch.go): wert = Gegenwert zum Listenpreis in USD (das Gewicht
 *  zwischen den Modellen), eingabe = input + cache, ausgabe = output. */
export interface Kosten {
  wert: number;
  eingabe: number;
  ausgabe: number;
}

/** Ein Chat in einem Wochenfenster des Abos; bis = Ende des Fensters. */
export interface ChatWoche {
  bis: string;
  modelle: Record<string, Kosten>;
  antworten: number;
}

export interface Chat {
  id: string;
  titel: string;
  repo: string;
  modell?: string;
  effort?: string;
  session_id?: string;
  /** von einer Seite angelegt: woran er arbeitet ("konstruktion:Kat/Name") */
  bezug?: string;
  /** von Hand ins Archiv (true) oder heraus (false); fehlt = von selbst */
  archiviert?: boolean;
  /** seit wann der Chat eine neue Claude-Sitzung hat (Tim 31.10.2026);
   *  der Verlauf davor steht zugeklappt darüber */
  neu_begonnen?: string;
  /** Verbrauch je Wochenfenster, jüngste zuerst, höchstens 5 */
  wochen?: ChatWoche[];
  erstellt: string;
  zuletzt: string;
  zustand: Zustand;
  seit?: string;
  /** offene Rückfragen ohne Inhalt; den Inhalt bringt ChatVerbindung.offen */
  offen?: RueckfrageKurz[];
  meldung?: string;
  seq: number;
  prozess: boolean;
  /** laufende Hintergrund-Aufgaben (Helfer, Befehle) -- der Chat arbeitet
   *  dann, auch wenn Claudes Antwort fertig ist (internal/claude) */
  hintergrund?: number;
  /** Commits dieses Chats (Trailer „Homelab-Chat: <id>"), jüngste zuerst */
  commits?: Commit[];
}

export interface Commit {
  hash: string;
  kurz: string;
  zeit: string;
  titel: string;
  gepusht: boolean;
  autor?: string;
  /** Trailer „Homelab-Chat": aus welchem Chat */
  chat?: string;
}

/** Was ein Commit geändert hat oder was an einer offenen Datei noch nicht
 *  committet ist (GET /v2-api/claude/repos/{repo}/diff). */
export interface Diff {
  repo: string;
  commit?: Commit;
  nachricht?: string;
  datei?: string;
  /** Unified-Format, wie git es schreibt (zerlegeDiff) */
  text: string;
  gekuerzt?: boolean;
}

export interface RepoStand {
  repo: string;
  zweig?: string;
  voraus: number;
  zurueck: number;
  offen: { pfad: string; art: string }[];
  offen_zahl: number;
  /** Commits, die der Server noch nicht hat (bis 20, jüngste zuerst) */
  ungepusht?: Commit[];
  /** die letzten 30 Commits des Zweigs, jüngste zuerst */
  verlauf?: Commit[];
  web?: string;
  fehler?: string;
}

/** Ein Modell, wie Claude Code es für das Konto anbietet. */
export interface Modell {
  wert: string;
  modell: string;
  name: string;
  beschreibung?: string;
  efforts?: string[];
}

export interface Fenster {
  anteil: number;
  bis?: string;
}

export interface Verbrauch {
  fuenf_stunden?: Fenster;
  woche?: Fenster;
  /** eigene Wochenfenster einzelner Modelle („Fable") */
  modelle?: (Fenster & { name: string })[];
  gemeldet?: string;
  /** die Woche zu Beginn des heutigen Tages (internal/claude/tageslimit.go) */
  tag?: { seit: string; anteil: number };
}

export interface Stand {
  chats: Chat[];
  verbrauch: Verbrauch;
  repos: string[];
  repo_staende?: RepoStand[];
  modelle?: Modell[];
  /** was Claude Code ohne Wahl nimmt: Modell, Effort je Modell */
  standard?: { modell?: string; effort?: Record<string, string> };
  version?: string;
  anmeldung?: Anmeldung;
  /** wie schnell Claude je Stunde schreibt (internal/claude/tempo.go) */
  tempo?: Tempo;
  /** die Brücke erlaubt Werkzeuge selbst (internal/claude/freigaben.go) */
  ohne_rueckfrage?: boolean;
}

/** Eine Stunde des Tages (Ortszeit): Mediane aller Antworten darin. */
export interface TempoStunde {
  stunde: number;
  antworten: number;
  tok_s?: number;
  ttft_ms?: number;
}

export interface Tempo {
  /** 24, Index = Stunde */
  stunden: TempoStunde[];
  antworten: number;
  seit?: string;
  /** Median aller Antworten */
  tok_s?: number;
}

/** Ab so vielen Antworten zählt eine Stunde für die Empfehlung. */
export const TEMPO_STUNDE_MIN = 3;

/** Der Satz zum Tempo (Tim 19.10.2026: „Gibt es bei Claude noch eine
 *  Empfehlung wann Claude am besten genutzt werden kann?"): gemessen an
 *  den eigenen Antworten, nicht geraten. Liegen die Stunden nah beieinander
 *  (unter 15 % Abstand), sagt er das -- eine schnellste Stunde wäre dann
 *  nur Zufall. */
export function tempoSatz(tp: Tempo | undefined): string {
  if (!tp || !tp.antworten) return "Noch keine Antwort gemessen.";
  const mittel = Math.round(tp.tok_s ?? 0);
  const zaehlen = tp.stunden.filter((s) => s.antworten >= TEMPO_STUNDE_MIN && s.tok_s);
  if (tp.antworten < 30 || zaehlen.length < 4) {
    return `${tp.antworten} Antworten gemessen, im Mittel ${mittel} Token/s – für eine Empfehlung noch zu wenig.`;
  }
  const best = zaehlen.reduce((a, b) => ((b.tok_s ?? 0) > (a.tok_s ?? 0) ? b : a));
  const langsam = zaehlen.reduce((a, b) => ((b.tok_s ?? 0) < (a.tok_s ?? 0) ? b : a));
  const abstand = ((best.tok_s ?? 0) - (langsam.tok_s ?? 0)) / (tp.tok_s || 1);
  if (abstand < 0.15) return `Über den Tag etwa gleich schnell: im Mittel ${mittel} Token/s.`;
  return `Am schnellsten um ${best.stunde} Uhr (${Math.round(best.tok_s ?? 0)} Token/s), am langsamsten um ${langsam.stunde} Uhr (${Math.round(langsam.tok_s ?? 0)}); im Mittel ${mittel}.`;
}

/** Der Entwurf im Feld, auf jedem Gerät derselbe (internal/claude/
 *  entwurf.go). Schlüssel: die Kennung des Chats oder „neu". */
export interface Entwurf {
  text: string;
  anhaenge: AnhangInfo[];
  /** nur beim neuen Chat */
  repo?: string;
  modell?: string;
  effort?: string;
  /** zählt jede Änderung */
  version: number;
  /** welches Fenster sie schrieb */
  fenster?: string;
  zeit?: string;
}

/** Was ein Fenster beim Tippen schickt. */
export interface EntwurfFelder {
  text: string;
  repo?: string;
  modell?: string;
  effort?: string;
}

/** Ob Claude Code beim Konto angemeldet ist (internal/claude/anmeldung.go).
 *  bekannt: geprüft seit dem Start der Brücke; laeuft: eine Anmeldung
 *  wartet auf den Code. */
export interface Anmeldung {
  bekannt: boolean;
  angemeldet: boolean;
  abo?: string;
  fehler?: string;
  seit?: string;
  laeuft?: boolean;
}

/** Abgemeldet: geprüft und nicht angemeldet -- dann arbeitet kein Chat. */
export function abgemeldet(a: Anmeldung | undefined): boolean {
  return !!a?.bekannt && !a.angemeldet;
}

/** Ein Anhang im Verlauf. */
export interface AnhangInfo {
  name: string;
  typ: string;
  groesse: number;
  datei: string;
}

// ─── Modell und Effort ──────────────────────────────────────────────

export const MODELLE = [
  { wert: "fable", name: "Fable" },
  { wert: "opus", name: "Opus" },
  { wert: "sonnet", name: "Sonnet" },
  { wert: "haiku", name: "Haiku" },
] as const;

export const EFFORTS = [
  { wert: "low", name: "niedrig" },
  { wert: "medium", name: "mittel" },
  { wert: "high", name: "hoch" },
  { wert: "xhigh", name: "sehr hoch" },
  { wert: "max", name: "max" },
] as const;

/** „opus" → „Opus", „claude-sonnet-5-5" → „Sonnet 5.5". */
export function modellName(m: string | undefined): string {
  if (!m) return "";
  const kurz = MODELLE.find((x) => x.wert === m);
  if (kurz) return kurz.name;
  // claude-opus-5-5 → Opus 5.5, claude-opus-5 → Opus 5; ein Datum hinten
  // (claude-haiku-4-5-20251001) ist keine Version
  const r = /^claude-([a-z]+)-(\d+)(?:-(\d{1,2}))?(?:-\d{8})?$/.exec(m);
  if (r) return `${r[1][0].toUpperCase()}${r[1].slice(1)} ${r[2]}${r[3] ? "." + r[3] : ""}`;
  return m;
}

export function effortName(e: string | undefined): string {
  return EFFORTS.find((x) => x.wert === e)?.name ?? (e || "");
}

/** Was „Standard" bedeutet: „Opus · sehr hoch". */
export function standardText(st: Stand["standard"]): string {
  const m = st?.modell ?? "";
  const effort = Object.entries(st?.effort ?? {}).find(([k]) => m && k.includes(m))?.[1];
  return [modellName(m), effortName(effort)].filter(Boolean).join(" · ");
}

/** Standard-Effort für ein Modell (Alias oder voller Name); leer, wenn
 *  die Einstellungen für dieses Modell keinen nennen. */
export function standardEffort(st: Stand["standard"], modell: string | undefined): string {
  const m = modell || st?.modell || "";
  if (!m) return "";
  const alias = MODELLE.find((x) => m.includes(x.wert))?.wert ?? m;
  return Object.entries(st?.effort ?? {}).find(([k]) => k.includes(alias))?.[1] ?? "";
}

// ─── Modelle aus dem Konto ──────────────────────────────────────────

/** Die Modelle für die Auswahl: „Standard" (der Eintrag default), die
 *  aktuellen (Kurzname wie opus) und ältere Versionen (voller Name). */
export function modellGruppen(ms: readonly Modell[] | undefined): { standard?: Modell; aktuell: Modell[]; aelter: Modell[] } {
  const liste = ms ?? [];
  return {
    standard: liste.find((m) => m.wert === "default"),
    aktuell: liste.filter((m) => m.wert !== "default" && !m.wert.startsWith("claude-")),
    aelter: liste.filter((m) => m.wert.startsWith("claude-")),
  };
}

/** Das Modell eines Chats in der Liste finden: über den Wert (opus,
 *  claude-opus-4-8) oder das aufgelöste Modell (claude-opus-5-5). */
export function findeModell(ms: readonly Modell[] | undefined, wert: string | undefined): Modell | undefined {
  if (!wert || !ms) return undefined;
  return ms.find((m) => m.wert === wert && m.wert !== "default") ?? ms.find((m) => m.modell === wert && m.wert !== "default");
}

/** Anzeige mit Version: „Opus 5.5". Ohne Liste wie modellName. */
export function modellAnzeige(ms: readonly Modell[] | undefined, wert: string | undefined): string {
  return findeModell(ms, wert)?.name ?? modellName(wert);
}

/** Welche Effort-Stufen das Modell kann; [] = keine Wahl (Haiku). */
export function effortsFuer(ms: readonly Modell[] | undefined, wert: string | undefined): string[] {
  const m = wert ? findeModell(ms, wert) : ms?.find((x) => x.wert === "default");
  if (m) return m.efforts ?? [];
  return EFFORTS.map((e) => e.wert);
}

// ─── Repo-Stand ─────────────────────────────────────────────────────

export function repoStandFuer(st: Stand | undefined, repo: string | undefined): RepoStand | undefined {
  return st?.repo_staende?.find((r) => r.repo === repo);
}

/** Lage eines Repos in Worten: „1 Commit nicht gepusht", „3 Dateien
 *  offen". Nicht gepusht ist ein Handgriff (warn); offene Dateien sind
 *  Arbeit im Gang (leise). */
export function repoLage(r: RepoStand | undefined): { text: string; ton: "" | "warn" }[] {
  if (!r) return [];
  const aus: { text: string; ton: "" | "warn" }[] = [];
  if (r.voraus > 0) aus.push({ text: r.voraus === 1 ? "1 Commit nicht gepusht" : `${r.voraus} Commits nicht gepusht`, ton: "warn" });
  if (r.offen_zahl > 0) aus.push({ text: r.offen_zahl === 1 ? "1 Datei offen" : `${r.offen_zahl} Dateien offen`, ton: "" });
  return aus;
}

/** Ein Chat ist „älter", wenn er seit 14 Tagen ruht. */
/** Im Archiv (Tim 19.10.2026, Archivordner je Repo: „am besten beides"):
 *  von Hand hineingelegt, oder von selbst nach AELTER_TAGE Ruhe -- es sei
 *  denn, von Hand herausgeholt. Wer arbeitet, wartet oder abgebrochen
 *  hat, steht nie im Archiv. Eine neue Nachricht holt ihn heraus (Brücke). */
export function imArchiv(c: Chat, jetzt: Date): boolean {
  if (c.zustand !== "ruht") return false;
  if (c.archiviert === true) return true;
  if (c.archiviert === false) return false;
  return istAelter(c, jetzt);
}

export const AELTER_TAGE = 14;
export function istAelter(c: Chat, jetzt: Date): boolean {
  return c.zustand === "ruht" && jetzt.getTime() - new Date(c.zuletzt).getTime() > AELTER_TAGE * 86_400_000;
}

// ─── Baum in Abschnitten (lib/claude/baum.svelte.ts) ────────────────

/** Das Repo des Chats (oder der Diff-Seite) hinter einem Pfad. */
export function repoVonPfad(st: Stand, pfad: string): string {
  const a = /^\/claude\/aenderung\/([^/?#]+)\//.exec(pfad);
  if (a) {
    const name = decodeURIComponent(a[1]);
    return st.repos.find((r) => repoName(r) === name) ?? "";
  }
  const m = /^\/claude\/([^/?#]+)/.exec(pfad);
  if (!m || m[1] === "neu") return "";
  const id = decodeURIComponent(m[1]);
  return st.chats.find((c) => c.id === id)?.repo ?? "";
}

/** Welches Repo „Änderungen" zeigt: das im Baum gewählte, solange derselbe
 *  Tab aktiv ist; sonst das des Chats im aktiven Tab; sonst das zuletzt
 *  gewählte; sonst das, in dem zuletzt gearbeitet wurde. */
export function repoFuerAenderungen(st: Stand, pfad: string, wahl: { repo: string; pfad: string } | null): string {
  if (wahl && wahl.pfad === pfad) return wahl.repo;
  const ausTab = repoVonPfad(st, pfad);
  if (ausTab) return ausTab;
  if (wahl) return wahl.repo;
  const juengster = [...st.chats].sort((a, b) => b.zuletzt.localeCompare(a.zuletzt))[0];
  return juengster?.repo ?? st.repos[0] ?? "";
}

/** Läuft gerade (lib/laeuft.ts): je Chat, der arbeitet oder wartet, ein
 *  Eintrag; die Leiste fasst sie als „Claude 2" zusammen, die Liste beim
 *  Klick nennt jeden. */
export function claudeLaeuft(chats: readonly Chat[]): Laeuft[] {
  const aus: Laeuft[] = [];
  for (const c of chats) {
    if (c.zustand !== "arbeitet" && c.zustand !== "wartet") continue;
    const wartet = c.zustand === "wartet";
    // Ein Chat an einer Konstruktion „konstruiert" (Tim 18.10.2026: „Wenn
    // Claude konstruiert, kann dann unter Claude konstruiert stehen?") und
    // führt zu ihr -- dort steht der Chat neben dem Modell.
    const k = konstruktionVonBezug(c.bezug);
    const was = wartet ? "wartet auf dich" : k ? "konstruiert" : "arbeitet";
    aus.push({
      id: "claude:" + c.id,
      gruppe: "claude",
      name: "Claude",
      was,
      wasViele: wartet ? "warten auf dich" : k ? "konstruieren" : "arbeiten",
      bezeichnung: k ? `${k.name} · ${c.titel}` : c.titel,
      pfad: k ? k.pfad : "/claude/" + c.id,
      ...(k ? { pfade: ["/claude/" + c.id] } : {}),
      ton: wartet ? "warn" : "laeuft",
      nurName: true,
      titel: k ? `Claude ${was} an „${k.name}“: ${c.titel}` : `Claude ${was}: ${c.titel}`,
    });
  }
  return aus;
}

/** Bezug „konstruktion:Kategorie/Name" → Name und Seite der Konstruktion
 *  (docs/konstruktionen.md, „Chat an der Konstruktion"). */
export function konstruktionVonBezug(bezug: string | undefined): { name: string; pfad: string } | null {
  const rest = bezug?.startsWith("konstruktion:") ? bezug.slice("konstruktion:".length) : "";
  const i = rest.lastIndexOf("/");
  if (i <= 0 || i === rest.length - 1) return null;
  const kategorie = rest.slice(0, i);
  const name = rest.slice(i + 1);
  return { name, pfad: `/werkstatt/konstruktionen/${encodeURIComponent(kategorie)}/${encodeURIComponent(name)}` };
}

/** Lage im Kopf „Chats": abgemeldet, was wartet, was läuft. */
export function chatsLageAus(chats: readonly Chat[], anmeldung?: Anmeldung): LageTeil[] {
  const wartet = chats.filter((c) => c.zustand === "wartet").length;
  const arbeitet = chats.filter((c) => c.zustand === "arbeitet").length;
  const aus: LageTeil[] = [];
  if (abgemeldet(anmeldung)) aus.push({ text: "abgemeldet", ton: "danger" });
  if (wartet) aus.push({ text: `${wartet} wartet`, ton: "warn" });
  if (arbeitet) aus.push({ text: `${arbeitet} arbeitet`, ton: "accent" });
  return aus;
}

/** Lage im Kopf „Änderungen": welches Repo, und was dort nicht gesichert ist. */
export function aenderungenLageAus(st: Stand, repo: string): LageTeil[] {
  if (!repo) return [];
  const aus: LageTeil[] = [{ text: repoName(repo) }];
  const rs = repoStandFuer(st, repo);
  if (!rs) return aus;
  if (rs.fehler) return [...aus, { text: "nicht lesbar", ton: "danger" }];
  if (rs.zweig && rs.zweig !== "main" && rs.zweig !== "master") aus.push({ text: rs.zweig });
  if (rs.voraus > 0) aus.push({ text: `${rs.voraus} nicht gepusht`, ton: "warn" });
  if (rs.zurueck > 0) aus.push({ text: `${rs.zurueck} auf dem Server neuer` });
  if (rs.offen_zahl > 0) aus.push({ text: `${rs.offen_zahl} offen` });
  if (aus.length === 1) aus.push({ text: "sauber", ton: "ok" });
  return aus;
}

/** Lage im Kopf „Commits": welches Repo, wann zuletzt. */
export function commitsLageAus(st: Stand, repo: string, jetzt: Date): LageTeil[] {
  if (!repo) return [];
  const letzter = repoStandFuer(st, repo)?.verlauf?.[0];
  return [{ text: repoName(repo) }, { text: letzter ? `zuletzt ${wannKurz(letzter.zeit, jetzt)}` : "noch keine Commits" }];
}

// ─── Diff (pages/ClaudeAenderung.svelte) ────────────────────────────

/** Pfad der Diff-Seite: ein Commit oder eine offene Datei eines Repos. */
export function aenderungPfad(repo: string, ziel: { commit: string } | { datei: string }): string {
  const z = "commit" in ziel ? ziel.commit : "datei:" + ziel.datei;
  return `/claude/aenderung/${encodeURIComponent(repoName(repo))}/${encodeURIComponent(z)}`;
}

/** Umkehrung des Ziels aus aenderungPfad. */
export function aenderungZiel(ziel: string): { commit?: string; datei?: string } {
  return ziel.startsWith("datei:") ? { datei: ziel.slice(6) } : { commit: ziel };
}

export interface DiffZeile {
  art: "+" | "-" | " " | "\\";
  text: string;
  alt?: number;
  neu?: number;
}
export interface DiffStueck {
  kopf: string;
  zeilen: DiffZeile[];
}
export interface DiffDatei {
  alt: string;
  neu: string;
  art: "geändert" | "neu" | "gelöscht" | "umbenannt" | "binär";
  plus: number;
  minus: number;
  stuecke: DiffStueck[];
}

const STUECK_KOPF = /^@@ -(\d+)(?:,\d+)? \+(\d+)(?:,\d+)? @@(.*)$/;

/** Zerlegt einen Unified-Diff (git diff/show) in Dateien und Stücke, mit
 *  Zeilennummern alt/neu. Was vor der ersten Datei steht, fällt weg. */
export function zerlegeDiff(text: string): DiffDatei[] {
  const dateien: DiffDatei[] = [];
  let d: DiffDatei | null = null;
  let s: DiffStueck | null = null;
  let alt = 0;
  let neu = 0;
  const pfad = (p: string) => p.replace(/^"|"$/g, "").replace(/^[ab]\//, "");
  for (const z of text.split("\n")) {
    if (z.startsWith("diff --git ")) {
      const m = /^diff --git a\/(.+) b\/(.+)$/.exec(z);
      d = { alt: m?.[1] ?? "", neu: m?.[2] ?? "", art: "geändert", plus: 0, minus: 0, stuecke: [] };
      dateien.push(d);
      s = null;
      continue;
    }
    if (!d) continue;
    if (!s) {
      if (z.startsWith("new file mode")) d.art = "neu";
      else if (z.startsWith("deleted file mode")) d.art = "gelöscht";
      else if (z.startsWith("rename from ")) (d.art = "umbenannt"), (d.alt = z.slice(12));
      else if (z.startsWith("rename to ")) d.neu = z.slice(10);
      else if (z.startsWith("Binary files ")) d.art = "binär";
      else if (z.startsWith("--- ") && z !== "--- /dev/null") d.alt = pfad(z.slice(4));
      else if (z.startsWith("+++ ") && z !== "+++ /dev/null") d.neu = pfad(z.slice(4));
    }
    const k = STUECK_KOPF.exec(z);
    if (k) {
      alt = Number(k[1]);
      neu = Number(k[2]);
      s = { kopf: k[3].trim(), zeilen: [] };
      d.stuecke.push(s);
      continue;
    }
    if (!s) continue;
    if (z.startsWith("+")) {
      d.plus++;
      s.zeilen.push({ art: "+", text: z.slice(1), neu: neu++ });
    } else if (z.startsWith("-")) {
      d.minus++;
      s.zeilen.push({ art: "-", text: z.slice(1), alt: alt++ });
    } else if (z.startsWith(" ")) {
      s.zeilen.push({ art: " ", text: z.slice(1), alt: alt++, neu: neu++ });
    } else if (z.startsWith("\\")) {
      s.zeilen.push({ art: "\\", text: z.slice(1).trim() });
    }
  }
  return dateien;
}

/** Lage im Kopf „Verbrauch" (zugeklappt): nur, was gerade verbraucht ist
 *  -- das eigene Tageslimit „Heute 40 %" vorn (Tim 01.11.2026), dann
 *  „Aktuell 73 %" (Tim 16.10.2026: „Dann reicht das Aktuelle"). Woche
 *  oder ein Modell kommen nur dazu, wenn sie ein Befund sind (ab 80 %),
 *  und dann vor „Aktuell": zugeklappt darf nichts Gelbes verschwinden. */
export function verbrauchLageAus(v: Verbrauch | undefined, tl?: Tageslimit): LageTeil[] {
  if (!v) return [];
  // Feste Wörter wie „Woche" und „Aktuell" unten: claude.ts bleibt ohne
  // Stores (Node-Tests, Notzugang), darum ohne t().
  const heute = tl ? [lageTeil("Heute", tl.anteil)] : [];
  return [...heute, ...verbrauchBefunde(v)];
}

/** „Heute 40 %" als Teil eines Lage-Satzes, Farbe nach verbrauchTon. */
export function lageTeil(name: string, anteil: number | undefined): LageTeil {
  return teilAus(name, { anteil: anteil ?? 0 });
}

function teilAus(name: string, f: Fenster): LageTeil {
  const ton = verbrauchTon(f.anteil);
  return { text: `${name} ${prozent(f.anteil)}`, ton: ton === "danger" ? "danger" : ton === "warn" ? "warn" : undefined };
}

function verbrauchBefunde(v: Verbrauch): LageTeil[] {
  const teil = teilAus;
  const befunde = [
    { name: "Woche", f: v.woche },
    ...(v.modelle ?? []).map((m) => ({ name: m.name, f: m as Fenster | undefined })),
  ]
    .filter((x): x is { name: string; f: Fenster } => !!x.f && verbrauchTon(x.f.anteil) !== "")
    .map((x) => teil(x.name, x.f));
  return [...befunde, ...(v.fuenf_stunden ? [teil("Aktuell", v.fuenf_stunden)] : [])];
}

export interface ClaudeAntwort {
  verbunden: boolean;
  stand: Stand;
}

export interface Ereignis {
  chat?: string;
  seq?: number;
  zeit: string;
  /** „offen": die offenen Rückfragen ganz (nur Live-Socket, internal/api/claude.go artOffen) */
  art: "claude" | "du" | "entscheidung" | "zustand" | "teil" | "stand" | "entwurf" | "offen";
  roh?: Record<string, unknown>;
  text?: string;
  daten?: Record<string, unknown>;
}

// ─── Kontext bis zur Komprimierung ──────────────────────────────────

/** Was Claude Code vor der Grenze freihält: Platz für die Antwort (20 k)
 *  und den Auto-Komprimierungs-Puffer (13 k). Gemessen, nicht geraten: die
 *  sechs Komprimierungen bis 19.10.2026 (Opus 5.5, Fenster 1 000 000)
 *  begannen bei 966 952 bis 973 887 Token (`compact_boundary.pre_tokens`)
 *  -- die Grenze 967 000 wird mit der nächsten Antwort überschritten. */
export const KOMPRIMIER_PUFFER = 33_000;

/** Belegter Kontext eines Chats und die Grenze, an der komprimiert wird. */
export interface Kontext {
  belegt: number;
  grenze: number;
  /** 0..1: belegt / grenze */
  anteil: number;
}

type RohUsage = { input_tokens?: number; cache_read_input_tokens?: number; cache_creation_input_tokens?: number; output_tokens?: number };

/** Der Kontext aus dem Verlauf (Tim 19.10.2026: „wieviel noch übrig ist,
 *  bevor der Chat komprimiert werden muss"): belegt = was die letzte
 *  Antwort des Chats selbst (nicht einer Teilaufgabe) gelesen und
 *  geschrieben hat -- so zählt Claude Code; nach einer Komprimierung, die
 *  neuer ist, deren `post_tokens`. Das Fenster des Modells steht im letzten
 *  `result` (`modelUsage.<modell>.contextWindow`); vor dem ersten gibt es
 *  keinen Kontext (null). Liest von hinten, hört beim ersten Treffer auf. */
export function kontextAus(ereignisse: readonly Ereignis[]): Kontext | null {
  // Ein „neu begonnen" zieht die Linie: was davor kam, gehörte zur alten
  // Sitzung (Tim 31.10.2026). Danach noch nichts -> kein Kontext.
  let von = 0;
  for (let i = ereignisse.length - 1; i >= 0; i--) {
    if (ereignisse[i].art === "zustand" && ereignisse[i].text === "neu begonnen") {
      von = i + 1;
      break;
    }
  }

  let belegt: number | null = null;
  let modell = "";
  let mu: Record<string, { contextWindow?: number }> | null = null;
  for (let i = ereignisse.length - 1; i >= von && (belegt == null || !modell || !mu); i--) {
    const e = ereignisse[i];
    if (e.art !== "claude" || !e.roh) continue;
    const z = e.roh as {
      type?: string;
      subtype?: string;
      parent_tool_use_id?: string | null;
      message?: { model?: string; usage?: RohUsage };
      compact_metadata?: { post_tokens?: number };
      modelUsage?: Record<string, { contextWindow?: number }>;
    };
    if (z.type === "assistant" && !z.parent_tool_use_id && z.message?.usage) {
      if (belegt == null) {
        const u = z.message.usage;
        belegt = (u.input_tokens ?? 0) + (u.cache_read_input_tokens ?? 0) + (u.cache_creation_input_tokens ?? 0) + (u.output_tokens ?? 0);
      }
      if (!modell) modell = z.message.model ?? "";
    } else if (z.type === "system" && z.subtype === "compact_boundary" && belegt == null) {
      belegt = z.compact_metadata?.post_tokens ?? 0;
    } else if (z.type === "result" && z.modelUsage && !mu) {
      mu = z.modelUsage;
    }
  }
  if (!mu) return null;
  // Das Modell des Chats; kennt man es nicht, das größte Fenster
  // (Nebenmodelle für Titel o. ä. haben kleinere).
  const fenster = (modell && mu[modell]?.contextWindow) || Math.max(0, ...Object.values(mu).map((m) => m.contextWindow ?? 0));
  if (belegt == null || fenster <= KOMPRIMIER_PUFFER) return null;
  const grenze = fenster - KOMPRIMIER_PUFFER;
  return { belegt, grenze, anteil: Math.min(1, belegt / grenze) };
}

/** „78 % frei" -- wie viel bis zur Komprimierung bleibt. */
export function kontextText(k: Kontext): string {
  return `${Math.max(0, Math.round((1 - k.anteil) * 100))} % frei`;
}

// ─── Kleinigkeiten ──────────────────────────────────────────────────

/** „/mnt/cache/data/homelab-dash-v2" → „homelab-dash-v2". */
export function repoName(pfad: string): string {
  const t = pfad.replace(/\/+$/, "").split("/");
  return t[t.length - 1] || pfad;
}

/** Pfad im Repo kurz: „/mnt/cache/data/homelab-dash-v2/web/src/x.ts" →
 *  „web/src/x.ts"; fremde Pfade bleiben, wie sie sind. */
export function kurzPfad(pfad: string, repos: readonly string[]): string {
  for (const r of repos) {
    const p = r.replace(/\/+$/, "") + "/";
    if (pfad.startsWith(p)) return pfad.slice(p.length);
  }
  return pfad;
}

const zwei = (n: number) => String(n).padStart(2, "0");
const TAGE = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** Wann zuletzt, für die Baumzeile: „14:10" heute, „gestern", der
 *  Wochentag in den letzten sechs Tagen, sonst „12.10.". */
export function wannKurz(iso: string, jetzt: Date): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const tag = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((tag(jetzt) - tag(d)) / 86_400_000);
  if (diff <= 0) return `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
  if (diff === 1) return "gestern";
  if (diff < 7) return TAGE[d.getDay()];
  return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.`;
}

/** „wieder voll um 17:00" / „wieder voll Mo 08:00". */
export function wiederVoll(bis: string | undefined, jetzt: Date): string {
  if (!bis) return "";
  const d = new Date(bis);
  if (isNaN(d.getTime())) return "";
  const uhr = `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
  const heute = d.toDateString() === jetzt.toDateString();
  return heute ? `wieder voll um ${uhr}` : `wieder voll ${TAGE[d.getDay()]} ${uhr}`;
}

/** Wann ein Fenster zurückgesetzt wird, kurz: „19:00" heute, sonst
 *  „Mo 08:00". Leer ohne Zeit. */
export function uhrBis(bis: string | undefined, jetzt: Date): string {
  if (!bis) return "";
  const d = new Date(bis);
  if (isNaN(d.getTime())) return "";
  const uhr = `${zwei(d.getHours())}:${zwei(d.getMinutes())}`;
  return d.toDateString() === jetzt.toDateString() ? uhr : `${TAGE[d.getDay()]} ${uhr}`;
}

/** Das 5-Stunden-Fenster im Kopf eines Chats (Tim 19.10.2026: „Statt der
 *  5 h besser die Zeit anzeigen, wann es zurückgesetzt wird"): „67 % bis
 *  19:00", aufgebraucht „voll bis 19:00". Ohne Zeit wie bisher „5 h 67 %". */
export function fensterKurz(f: Fenster, jetzt: Date, woche = false): string {
  const zeit = uhrBis(f.bis, jetzt);
  const vor = woche ? "Woche " : "";
  if (!zeit) return `${woche ? "Woche" : "5 h"} ${prozent(f.anteil)}`;
  return (f.anteil ?? 0) >= 1 ? `${vor}voll bis ${zeit}` : `${vor}${prozent(f.anteil)} bis ${zeit}`;
}

/** Das Fenster für den Kopf eines Chats: das 5-Stunden-Fenster, sonst die
 *  Woche (Tim 31.10.2026: Max 10× hat kein 5-Stunden-Fenster mehr, nur das
 *  Wochenlimit -- der Kopf zeigte dann gar nichts). */
export function kopfFenster(v: { fuenf_stunden?: Fenster; woche?: Fenster } | undefined): { f: Fenster; woche: boolean } | undefined {
  if (v?.fuenf_stunden) return { f: v.fuenf_stunden, woche: false };
  if (v?.woche) return { f: v.woche, woche: true };
  return undefined;
}

/** Das eigene Tageslimit (Tim 01.11.2026: „wie viel ich jeden Tag nutzen
 *  kann. Dabei hätte ich am Samstag gerne noch 20 % über"). */
export interface Tageslimit {
  /** wie viel vom Limit heute verbraucht ist, 0..1 (darüber: überzogen) */
  anteil: number;
  /** was heute zusteht, als Anteil der Woche */
  limit: number;
  /** heute verbraucht, als Anteil der Woche */
  heute: number;
  /** heute ist der letzte Tag der Woche: dann steht der ganze Rest zu */
  letzterTag: boolean;
  /** der letzte Tag der Woche kurz („Sa") */
  reserveAm: string;
}

const mitternacht = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const datumIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** Was heute vom Wochenlimit zusteht: der Rest bis zur Reserve, gleich
 *  verteilt auf die Tage bis vor dem letzten Tag der Woche. Gerechnet ab
 *  dem Stand zu Beginn des Tages -- wer gestern gespart hat, darf heute
 *  mehr, wer überzogen hat, weniger. Der letzte Tag (Samstag, die Woche
 *  endet So 0:00) bekommt, was übrig ist, die Reserve eingeschlossen.
 *  reserve als Anteil (0,2). Ohne Wochenfenster nichts. */
export function tageslimit(v: Verbrauch | undefined, reserve: number, jetzt: Date): Tageslimit | undefined {
  const w = v?.woche;
  const ende = w?.bis ? new Date(w.bis) : undefined;
  if (!w || !ende || isNaN(ende.getTime()) || ende <= jetzt) return undefined;
  const heute = mitternacht(jetzt);
  // Den Beginn meldet die Brücke mit dem ersten Wert des Tages; fehlt er
  // für heute, kam seit Mitternacht keiner -- dann ist nichts verbraucht.
  // Verglichen wird das Datum, wie die Brücke es schreibt („2026-11-01T00:00
  // +01:00"), nicht der Zeitpunkt: so stört eine andere Zeitzone nicht.
  // Begann die Woche heute (So 0:00), ist alles in ihr von heute.
  const ersterTag = ende.getTime() - 7 * 86_400_000 >= heute.getTime();
  const beginn = ersterTag ? 0 : v?.tag && v.tag.seit.slice(0, 10) === datumIso(heute) ? Math.min(v.tag.anteil, w.anteil) : w.anteil;
  // Die Woche endet zu einer festen UTC-Zeit: im Winter So 0:00, im Sommer
  // So 1:00. Die Stunde am Sonntag ist kein eigener Tag -- letzter Tag ist
  // der, in dem es 6 h vor dem Ende ist.
  const letzter = mitternacht(new Date(ende.getTime() - 6 * 3_600_000));
  const letzterTag = heute.getTime() >= letzter.getTime();
  const r = Math.min(Math.max(reserve, 0), 0.9);
  // Tage bis vor den letzten (mit heute); ohne Reserve zählt er mit.
  const tage = Math.round((letzter.getTime() - heute.getTime()) / 86_400_000) + (r > 0 ? 0 : 1);
  const limit = letzterTag ? Math.max(0, 1 - beginn) : Math.max(0, 1 - r - beginn) / Math.max(1, tage);
  const verbraucht = Math.max(0, w.anteil - beginn);
  return {
    // Nichts mehr frei (vor Samstag schon über der Reserve): voll, rot.
    anteil: limit > 0.0005 ? verbraucht / limit : 1,
    limit,
    heute: verbraucht,
    letzterTag,
    reserveAm: TAGE[letzter.getDay()],
  };
}

/** Farbe erst bei Befund: grau bis 80 %, dann gelb, ab 95 % rot. */
export function verbrauchTon(anteil: number | undefined): "" | "warn" | "danger" {
  if (anteil == null) return "";
  if (anteil >= 0.95) return "danger";
  if (anteil >= 0.8) return "warn";
  return "";
}

export function prozent(anteil: number | undefined): string {
  if (anteil == null) return "—";
  return `${Math.round(anteil * 100)} %`;
}

/** Eine Zeile der Tabelle „Verbrauch je Chat" (Tim 31.10.2026: das Wochen-
 *  limit ist knapp, er will sehen, welche Chats es verbrauchen). */
export interface VerbrauchZeile {
  chat: Chat;
  /** Gegenwert zum Listenpreis in USD (costUSD): das Gewicht zwischen den Modellen */
  wert: number;
  /** Anteil an allen Chats dieser Woche, 0..1 */
  anteil: number;
  /** Modelle des Chats, die teuersten zuerst */
  modelle: { modell: string; wert: number }[];
  eingabe: number;
  ausgabe: number;
  antworten: number;
}

/** Die Wochenfenster, für die es Zahlen gibt (Ende, ms), jüngste zuerst. */
export function verbrauchsWochen(chats: readonly Chat[]): number[] {
  const enden = new Set<number>();
  for (const c of chats) for (const w of c.wochen ?? []) if (Number.isFinite(Date.parse(w.bis))) enden.add(Date.parse(w.bis));
  return [...enden].sort((a, b) => b - a);
}

/** Die Chats in der Woche mit dem Ende bis (ms), mit ihrem Anteil am
 *  Verbrauch aller Chats; die größten zuerst. Helfer zählen schon bei ihrem
 *  Chat mit (die Brücke rechnet das modelUsage des Chats). */
export function verbrauchJeChat(chats: readonly Chat[], bis: number): VerbrauchZeile[] {
  const zeilen: VerbrauchZeile[] = [];
  for (const chat of chats) {
    const w = chat.wochen?.find((x) => Date.parse(x.bis) === bis);
    if (!w) continue;
    const modelle = Object.entries(w.modelle ?? {})
      .map(([modell, k]) => ({ modell, wert: k.wert }))
      .sort((a, b) => b.wert - a.wert);
    const ks = Object.values(w.modelle ?? {});
    zeilen.push({
      chat,
      wert: ks.reduce((n, k) => n + k.wert, 0),
      anteil: 0,
      modelle,
      eingabe: ks.reduce((n, k) => n + k.eingabe, 0),
      ausgabe: ks.reduce((n, k) => n + k.ausgabe, 0),
      antworten: w.antworten,
    });
  }
  const summe = zeilen.reduce((n, z) => n + z.wert, 0);
  for (const z of zeilen) z.anteil = summe > 0 ? z.wert / summe : 0;
  return zeilen.sort((a, b) => b.wert - a.wert || a.chat.id.localeCompare(b.chat.id));
}

/** Token kurz und deutsch: 12345678 → „12,3 Mio", 45600 → „45,6 Tsd". */
export function tokenKurz(n: number): string {
  const f = (x: number) => x.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  if (n >= 1_000_000) return `${f(n / 1_000_000)} Mio`;
  if (n >= 1_000) return `${f(n / 1_000)} Tsd`;
  return String(Math.round(n));
}

/** Chats je Repo, in der Reihenfolge der Repos; der neueste Chat oben. */
export function chatsJeRepo(st: Stand): { repo: string; name: string; chats: Chat[] }[] {
  const aus = st.repos.map((r) => ({ repo: r, name: repoName(r), chats: [] as Chat[] }));
  for (const c of st.chats) {
    let g = aus.find((x) => x.repo === c.repo);
    if (!g) {
      g = { repo: c.repo, name: repoName(c.repo), chats: [] };
      aus.push(g);
    }
    g.chats.push(c);
  }
  for (const g of aus) g.chats.sort((a, b) => b.zuletzt.localeCompare(a.zuletzt));
  return aus;
}

/** Zustand in Worten, für Baum und Kopf. */
/** „2 Helfer im Hintergrund" -- für den Kopf des Chats; leer ohne. */
export function hintergrundText(c: Chat): string {
  const n = c.hintergrund ?? 0;
  if (n <= 0) return "";
  return n === 1 ? "1 Helfer im Hintergrund" : `${n} Helfer im Hintergrund`;
}

export function zustandText(c: Chat): string {
  switch (c.zustand) {
    case "arbeitet":
      return "arbeitet";
    case "wartet":
      return (c.offen?.length ?? 0) > 1 ? `wartet: ${c.offen?.length} Freigaben` : "wartet auf dich";
    case "fehler":
      return "abgebrochen";
    default:
      return "";
  }
}

// ─── Rückfragen ─────────────────────────────────────────────────────

export interface FrageText {
  /** „Claude möchte einen Befehl ausführen" */
  titel: string;
  /** das Ding in Monospace: der Befehl, die Datei, die Adresse */
  ding: string;
  /** Claudes eigene Beschreibung, wenn es eine gibt */
  warum: string;
  /** Text für „Immer": „make -C … deploy", leer = keine Regel möglich */
  regel: string;
}

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

/** Was Claude möchte, nur aus dem Werkzeug -- steht auch ohne Inhalt fest
 *  (freier Stand, RueckfrageKurz). */
export function frageTitel(werkzeug: string): string {
  switch (werkzeug) {
    case "Bash":
      return "Claude möchte einen Befehl ausführen";
    case "Edit":
    case "MultiEdit":
    case "Write":
    case "NotebookEdit":
      return "Claude möchte eine Datei ändern";
    case "Read":
      return "Claude möchte eine Datei lesen";
    case "WebFetch":
      return "Claude möchte eine Seite abrufen";
    case "WebSearch":
      return "Claude möchte im Netz suchen";
    case "AskUserQuestion":
      return "Claude fragt dich";
    case "ExitPlanMode":
      return "Claude möchte mit dem Plan anfangen";
    default: {
      const m = mcpTeile(werkzeug);
      if (m) return `Claude möchte ${MCP_NAME[m.server] ?? m.server} benutzen: ${m.werkzeug}`;
      return `Claude möchte ${werkzeug} benutzen`;
    }
  }
}

export function frageText(r: Rueckfrage, repos: readonly string[] = []): FrageText {
  const e = r.eingabe ?? {};
  const warum = s(e.description) || r.text || "";
  const regel = regelText(r.vorschlag);
  const titel = frageTitel(r.werkzeug);
  switch (r.werkzeug) {
    case "Bash":
      return { titel, ding: s(e.command), warum, regel };
    case "Edit":
    case "MultiEdit":
    case "Write":
    case "NotebookEdit":
      return { titel, ding: kurzPfad(s(e.file_path) || s(e.notebook_path), repos), warum, regel };
    case "Read":
      return { titel, ding: kurzPfad(s(e.file_path), repos), warum, regel };
    case "WebFetch":
      return { titel, ding: s(e.url), warum: s(e.prompt) ? "" : warum, regel };
    case "WebSearch":
      return { titel, ding: s(e.query), warum, regel };
    case "AskUserQuestion":
      return { titel, ding: "", warum: "", regel: "" };
    case "ExitPlanMode":
      return { titel, ding: "", warum: s(e.plan), regel: "" };
    default:
      return { titel, ding: JSON.stringify(e), warum, regel };
  }
}

/** Die Freigaben unter dem Verlauf: WELCHE offen sind, sagt der Stand
 *  (live über den Hub, auch ohne offenen Chat-Socket); den INHALT bringt
 *  der Live-Socket des Chats hinter der PIN (Sicherheits-Nachprüfung
 *  21.10.2026, Befund 2). Fehlt er noch (Socket verbindet gerade), ist
 *  voll null -- dann ohne Knöpfe: erlaubt wird nie etwas, das man nicht
 *  gesehen hat. */
export function offeneFreigaben(
  kurz: readonly RueckfrageKurz[] | undefined,
  voll: readonly Rueckfrage[],
): { kurz: RueckfrageKurz; voll: Rueckfrage | null }[] {
  return (kurz ?? []).map((k) => ({ kurz: k, voll: voll.find((r) => r.id === k.id) ?? null }));
}

/** Der Regel-Vorschlag von Claude Code als Text: aus addRules der
 *  ruleContent, sonst der Name des Werkzeugs. */
export function regelText(vorschlag: unknown): string {
  if (!Array.isArray(vorschlag)) return "";
  for (const v of vorschlag) {
    const x = v as { type?: string; rules?: { toolName?: string; ruleContent?: string }[] };
    if (x?.type !== "addRules" || !Array.isArray(x.rules) || !x.rules.length) continue;
    const r = x.rules[0];
    return kuerzeBefehl(r.ruleContent || r.toolName || "", 48);
  }
  return "";
}

/** Lange Befehle in der Mitte kürzen: „make -C /mnt/…/homelab-dash-v2 deploy". */
export function kuerzeBefehl(b: string, n: number): string {
  const t = b.replace(/\s+/g, " ").trim();
  if (t.length <= n) return t;
  const vorn = Math.ceil((n - 1) * 0.55);
  const hinten = n - 1 - vorn;
  return t.slice(0, vorn) + "…" + t.slice(t.length - hinten);
}

/** Fragen einer AskUserQuestion-Rückfrage. */
export interface Frage {
  frage: string;
  kopf?: string;
  mehrfach: boolean;
  optionen: { label: string; text?: string }[];
}

export function fragenAus(r: Rueckfrage): Frage[] {
  const qs = (r.eingabe?.questions ?? []) as {
    question?: string;
    header?: string;
    multiSelect?: boolean;
    options?: { label?: string; description?: string }[];
  }[];
  if (!Array.isArray(qs)) return [];
  return qs
    .filter((q) => q?.question)
    .map((q) => ({
      frage: q.question!,
      kopf: q.header,
      mehrfach: !!q.multiSelect,
      optionen: (q.options ?? []).filter((o) => o?.label).map((o) => ({ label: o.label!, text: o.description })),
    }));
}

// ─── Verlauf → Darstellung ──────────────────────────────────────────

export interface Werkzeug {
  art: "werkzeug";
  key: string;
  name: string;
  eingabe: Record<string, unknown>;
  /** Ausgabe als Text (gekürzt von der Brücke) */
  ergebnis?: string;
  fehler: boolean;
  fertig: boolean;
  zeit: string;
}

export type Eintrag =
  | { art: "du"; key: string; text: string; zeit: string; anhaenge: AnhangInfo[]; ausApp: boolean }
  | { art: "text"; key: string; text: string; zeit: string }
  | Werkzeug
  | { art: "entscheidung"; key: string; erlaubt: boolean; immer: boolean; grund: string; werkzeug: string; zeit: string; anderswo: boolean }
  | { art: "hinweis"; key: string; text: string; ton: "" | "warn" | "danger" | "laeuft"; zeit: string }
  /** Dateien, die Claude dir geschickt hat (SendUserFile), als Kopie im Chat */
  | { art: "datei"; key: string; anhaenge: AnhangInfo[]; zeit: string }
  /** Trennlinie: ab hier eine neue Claude-Sitzung (Chat.neu_begonnen) */
  | { art: "neubeginn"; key: string; zeit: string };

/** So nennt die Brücke, was aus der Claude-App kommt (claude.VonApp). */
export const VON_APP = "App";

type Block = { type?: string; text?: string; id?: string; name?: string; input?: unknown; tool_use_id?: string; content?: unknown; is_error?: boolean };

function blockText(content: unknown): string {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content
      .map((c) => (typeof c === "string" ? c : (c as Block)?.type === "text" ? ((c as Block).text ?? "") : ""))
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

/** Aus dem gespeicherten Verlauf die Einträge des Chats. Werkzeug-Aufruf
 *  und sein Ergebnis werden EINE Zeile (über die tool_use_id). */
export function baueVerlauf(ereignisse: readonly Ereignis[]): Eintrag[] {
  return new VerlaufBauer().bau(ereignisse);
}

/** baueVerlauf Schritt für Schritt: liest nur, was seit dem letzten Mal
 *  dazukam, und lässt jeden fertigen Eintrag dasselbe Objekt bleiben. Vorher
 *  baute jedes Ereignis (bei arbeitendem Claude mehrere je Sekunde) den
 *  ganzen Verlauf neu -- neue Objekte, also rechnete die Fläche auch das
 *  Markdown jeder sichtbaren Antwort neu, und das Tippen im Feld hakte
 *  (Tim 20.10.2026; Messung: 10-16 ms und 1-1,7 MB Müll je Ereignis bei
 *  einem langen Chat). Ein Ergebnis zu einem Werkzeug ersetzt nur dessen
 *  Zeile. Kommt ein anderer Verlauf (neu geladen), fängt er von vorn an. */
export class VerlaufBauer {
  #aus: Eintrag[] = [];
  #werkzeuge = new Map<string, number>();
  #gesehen = new Set<string>();
  #quelle: readonly Ereignis[] | null = null;
  #n = 0;

  bau(ereignisse: readonly Ereignis[]): Eintrag[] {
    // Weitergeschrieben heißt: die bekannten Ereignisse stehen unverändert
    // vorn (ChatVerbindung hängt an; neu geladen ersetzt sie das Ganze).
    const q = this.#quelle;
    const weiter = q !== null && ereignisse.length >= this.#n && (this.#n === 0 || ereignisse[this.#n - 1] === q[this.#n - 1]);
    if (!weiter) {
      this.#aus = [];
      this.#werkzeuge.clear();
      this.#gesehen.clear();
      this.#n = 0;
    }
    for (let i = this.#n; i < ereignisse.length; i++) this.#nimm(ereignisse[i]);
    this.#quelle = ereignisse;
    this.#n = ereignisse.length;
    return this.#aus.slice();
  }

  #nimm(e: Ereignis): void {
    const k = `${e.seq ?? ""}`;
    if (e.art === "du") {
      const an = (e.daten?.anhaenge ?? []) as AnhangInfo[];
      this.#aus.push({ art: "du", key: "du" + k, text: e.text ?? "", zeit: e.zeit, anhaenge: Array.isArray(an) ? an : [], ausApp: e.daten?.von === VON_APP });
      return;
    }
    if (e.art === "entscheidung") {
      const d = (e.daten ?? {}) as { erlaubt?: boolean; immer?: boolean; grund?: string; werkzeug?: string; anderswo?: boolean; von?: string };
      // Von der Brücke selbst erlaubt (Einstellung „ohne Rückfrage"): keine
      // Zeile -- es gab ja nichts zu entscheiden.
      if (d.von === "selbst") return;
      this.#aus.push({ art: "entscheidung", key: "ent" + k, erlaubt: !!d.erlaubt, immer: !!d.immer, grund: d.grund ?? "", werkzeug: d.werkzeug ?? "", zeit: e.zeit, anderswo: !!d.anderswo });
      return;
    }
    if (e.art === "zustand") {
      if (e.text === "geschickt") {
        const an = (e.daten?.anhaenge ?? []) as AnhangInfo[];
        if (Array.isArray(an) && an.length) this.#aus.push({ art: "datei", key: "dat" + k, anhaenge: an, zeit: e.zeit });
      } else if (e.text === "neu begonnen") {
        this.#aus.push({ art: "neubeginn", key: "nb" + k, zeit: e.zeit });
      } else if (e.text === "abgebrochen") {
        const m = s((e.daten ?? {}).meldung) || "Claude Code endete mitten in der Arbeit";
        this.#aus.push({ art: "hinweis", key: "z" + k, text: m, ton: "danger", zeit: e.zeit });
      } else if (e.text === "gestoppt") {
        this.#aus.push({ art: "hinweis", key: "z" + k, text: "gestoppt", ton: "", zeit: e.zeit });
      } else if (e.text === "fortgesetzt") {
        const d = (e.daten ?? {}) as { titel?: string; ausgelassen?: number };
        const davor = d.ausgelassen ? ` ${d.ausgelassen} frühere Einträge stehen nur dort.` : "";
        this.#aus.push({ art: "hinweis", key: "z" + k, text: `Fortgesetzt aus VS Code („${d.titel ?? ""}"), als eigene Abzweigung.${davor}`, ton: "", zeit: e.zeit });
      } else if (e.text === "modell") {
        const m = s((e.daten ?? {}).modell);
        this.#aus.push({ art: "hinweis", key: "z" + k, text: m ? `Modell: ${modellName(m)}` : "Modell: Standard", ton: "", zeit: e.zeit });
      } else if (e.text === "effort") {
        const f = s((e.daten ?? {}).effort);
        this.#aus.push({ art: "hinweis", key: "z" + k, text: `Effort: ${f ? effortName(f) : "Standard"} (ab der nächsten Nachricht)`, ton: "", zeit: e.zeit });
      }
      return;
    }
    if (e.art !== "claude" || !e.roh) return;
    const z = e.roh as { type?: string; subtype?: string; is_error?: boolean; message?: { id?: string; content?: unknown }; result?: string };
    if (z.type === "assistant") {
      const blocks = Array.isArray(z.message?.content) ? (z.message!.content as Block[]) : [];
      blocks.forEach((b, i) => {
        if (b.type === "text" && b.text?.trim()) {
          const key = `t${z.message?.id ?? k}:${i}:${b.text.length}`;
          if (this.#gesehen.has(key)) return;
          this.#gesehen.add(key);
          this.#aus.push({ art: "text", key, text: b.text, zeit: e.zeit });
        } else if (b.type === "tool_use" && b.id && !this.#werkzeuge.has(b.id)) {
          const w: Werkzeug = {
            art: "werkzeug",
            key: "w" + b.id,
            name: b.name ?? "?",
            eingabe: (b.input as Record<string, unknown>) ?? {},
            fehler: false,
            fertig: false,
            zeit: e.zeit,
          };
          this.#werkzeuge.set(b.id, this.#aus.length);
          this.#aus.push(w);
        }
      });
    } else if (z.type === "user") {
      const blocks = Array.isArray(z.message?.content) ? (z.message!.content as Block[]) : [];
      for (const b of blocks) {
        if (b.type !== "tool_result" || !b.tool_use_id) continue;
        const i = this.#werkzeuge.get(b.tool_use_id);
        if (i == null) continue;
        // Ein neues Objekt: nur diese Zeile zeichnet sich neu.
        this.#aus[i] = { ...(this.#aus[i] as Werkzeug), ergebnis: blockText(b.content), fehler: !!b.is_error, fertig: true };
      }
    } else if (z.type === "result" && z.subtype && z.subtype !== "success") {
      this.#aus.push({ art: "hinweis", key: "r" + k, text: "Die Antwort brach ab (" + z.subtype.replace(/_/g, " ") + ")", ton: "warn", zeit: e.zeit });
    } else if (z.type === "control_request") {
      // die Rückfrage selbst steht als Block am Ende, solange sie offen ist
    }
    }
}

// ─── Werkzeug-Zeile ─────────────────────────────────────────────────

export interface WerkzeugZeile {
  /** „Gelesen", „Geändert", „Befehl" */
  art: string;
  /** das Ding in Monospace */
  was: string;
  /** rechts: „+18 −2", „6 Treffer", „Fehler" */
  wie: string;
  // Symbole nach docs/symbole.md: `box` nur für SolidCraft (ein Teil), eine
  // Teilaufgabe ist ein weiterer Claude, Unbekanntes `dots-horizontal`.
  symbol: "file" | "search" | "pencil" | "terminal" | "globe" | "checks" | "box" | "paperclip" | "message" | "claude" | "dots-horizontal";
}

function zeilen(t: string): number {
  return t ? t.split("\n").length : 0;
}

/** Modell eines Helfers (Agent-Werkzeug, Feld model): „Haiku", „Sonnet" …;
 *  ohne Angabe „wie der Chat". */
export function helferModell(m: unknown): string {
  const t = typeof m === "string" ? m.trim() : "";
  if (!t) return "wie der Chat";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

export function werkzeugZeile(w: Werkzeug, repos: readonly string[] = []): WerkzeugZeile {
  const e = w.eingabe;
  const pfad = kurzPfad(s(e.file_path) || s(e.notebook_path) || s(e.path), repos);
  const fehler = w.fertig && w.fehler ? "Fehler" : "";
  switch (w.name) {
    case "Read":
      return { art: "Gelesen", was: pfad, wie: fehler, symbol: "file" };
    case "Grep": {
      const treffer = /Found (\d+)/.exec(w.ergebnis ?? "")?.[1];
      return { art: "Gesucht", was: `„${s(e.pattern)}"${pfad ? " in " + pfad : ""}`, wie: fehler || (treffer ? `${treffer} Treffer` : ""), symbol: "search" };
    }
    case "Glob":
      return { art: "Dateien", was: s(e.pattern), wie: fehler, symbol: "search" };
    case "Edit":
      return { art: "Geändert", was: pfad, wie: fehler || `+${zeilen(s(e.new_string))} −${zeilen(s(e.old_string))}`, symbol: "pencil" };
    case "MultiEdit":
      return { art: "Geändert", was: pfad, wie: fehler || `${(e.edits as unknown[] | undefined)?.length ?? 0} Stellen`, symbol: "pencil" };
    case "Write":
      return { art: "Geschrieben", was: pfad, wie: fehler || `${zeilen(s(e.content))} Zeilen`, symbol: "pencil" };
    case "NotebookEdit":
      return { art: "Geändert", was: pfad, wie: fehler, symbol: "pencil" };
    case "Bash":
      // Ohne Wort: das Symbol und der Befehl in Schreibmaschinenschrift sagen
      // es (Tim 19.10.2026: „Lässt sich das hier etwas kompakter darstellen?").
      return { art: "", was: s(e.command), wie: fehler || (w.fertig ? "" : "läuft"), symbol: "terminal" };
    case "WebFetch":
      return { art: "Abgerufen", was: s(e.url), wie: fehler, symbol: "globe" };
    case "WebSearch":
      return { art: "Im Netz", was: s(e.query), wie: fehler, symbol: "globe" };
    case "TodoWrite":
      return { art: "Aufgaben", was: `${(e.todos as unknown[] | undefined)?.length ?? 0} Punkte`, wie: "", symbol: "checks" };
    case "SendUserFile":
      return { art: "Geschickt", was: ((e.files as unknown[] | undefined) ?? []).map((f) => s(f).split("/").pop()).join(", "), wie: fehler, symbol: "paperclip" };
    case "Task":
    case "Agent":
      // Mit dem Modell des Helfers (Tim 31.10.2026: „in allen Chats zeigen,
      // welche Aufgabe an ein anderes Modell abgegeben wird"); ohne Angabe
      // erbt der Helfer das Modell des Chats.
      return { art: `Teilaufgabe · ${helferModell(e.model)}`, was: s(e.description) || s(e.prompt).slice(0, 80), wie: fehler || (w.fertig ? "" : "läuft"), symbol: "claude" };
    // Eine Frage an dich (Tim 19.10.2026: „Die Question ist hier im Feld noch
    // leer") -- die Frage selbst, nicht der Name des Werkzeugs.
    case "AskUserQuestion": {
      const fragen = (e.questions as { question?: unknown }[] | undefined) ?? [];
      const mehr = fragen.length > 1 ? ` (+${fragen.length - 1})` : "";
      return { art: "Frage", was: s(fragen[0]?.question) + mehr, wie: fehler || (w.fertig ? "beantwortet" : "wartet"), symbol: "message" };
    }
    case "ToolSearch":
      return { art: "Werkzeuge", was: s(e.query), wie: fehler, symbol: "search" };
    case "Skill":
      return { art: "Skill", was: s(e.skill) || s(e.name), wie: fehler, symbol: "file" };
    default:
      if (w.name.startsWith("mcp__")) return mcpZeile(w, fehler, repos);
      return { art: w.name, was: "", wie: fehler, symbol: "dots-horizontal" };
  }
}

/** Anzeigename eines MCP-Servers. */
const MCP_NAME: Record<string, string> = { solidcraft: "SolidCraft" };

/** Server und Werkzeug aus „mcp__<server>__<werkzeug>". */
export function mcpTeile(name: string): { server: string; werkzeug: string } | null {
  const m = /^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/.exec(name);
  return m ? { server: m[1], werkzeug: m[2] } : null;
}

function kurzJson(v: unknown, n = 90): string {
  const t = JSON.stringify(v ?? {});
  return t === "{}" ? "" : t.length > n ? t.slice(0, n - 1) + "…" : t;
}

/** Werkzeuge eines MCP-Servers (Tim 19.10.2026: „Bei dem Konstruktionschat
 *  fehlt noch der Inhalt in den Kästen") -- SolidCraft in Worten, andere
 *  Server mit ihren Eingaben. */
function mcpZeile(w: Werkzeug, fehler: string, repos: readonly string[]): WerkzeugZeile {
  const t = mcpTeile(w.name) ?? { server: "mcp", werkzeug: w.name };
  const art = MCP_NAME[t.server] ?? t.server;
  const e = w.eingabe;
  const laeuft = w.fertig ? "" : "läuft";
  if (t.server !== "solidcraft") return { art, was: `${t.werkzeug} ${kurzJson(e)}`.trim(), wie: fehler || laeuft, symbol: "dots-horizontal" };
  const befehle = (Array.isArray(e.commands) ? e.commands : []) as { command?: string }[];
  const pfad = kurzPfad(s(e.path), repos);
  let was = t.werkzeug;
  let wie = "";
  switch (t.werkzeug) {
    case "batch": {
      const namen = befehle.map((b) => s(b?.command));
      was = namen.length > 3 ? `${namen.slice(0, 3).join(", ")} … (${namen.length} Schritte)` : namen.join(", ");
      // Ergebnis: „completed": n, „failed": { index, command, error }
      const fehl = /"failed":\s*\{[^}]*"command":\s*"([^"]+)"/.exec(w.ergebnis ?? "")?.[1];
      wie = fehl ? `Fehler bei ${fehl}` : "";
      break;
    }
    case "execute":
      was = s(e.command) + (e.params ? " " + kurzJson(e.params, 60) : "");
      break;
    case "screenshot":
      was = `Bild${s(e.view) ? " · " + s(e.view) : e.camera ? " · eigene Ansicht" : ""}`;
      break;
    case "measure":
      was = `Messen${Array.isArray(e.bodies) && e.bodies.length ? " · " + (e.bodies as unknown[]).map(s).join(", ") : ""}`;
      break;
    case "body_topology":
      was = `Flächen und Kanten${s(e.body) ? " · " + s(e.body) : ""}`;
      break;
    case "set_parameter":
      was = `${s(e.name)} = ${s(e.expression) || s(e.value) || kurzJson(e.value, 30)}`;
      break;
    case "save":
      was = `Gespeichert${pfad ? " · " + pfad : ""}`;
      break;
    case "export":
      was = `Exportiert${pfad ? " · " + pfad : ""}${s(e.format) ? " (" + s(e.format) + ")" : ""}`;
      break;
    case "open":
      was = `Geöffnet${pfad ? " · " + pfad : ""}`;
      break;
    case "new_design":
      was = "Neue Konstruktion";
      break;
    case "list_commands":
      was = `Befehle${s(e.filter) || s(e.prefix) ? " · " + (s(e.filter) || s(e.prefix)) : ""}`;
      break;
    case "inspect_design":
      was = "Konstruktion ansehen";
      break;
    case "undo":
      was = "Rückgängig";
      break;
    case "redo":
      was = "Wiederholen";
      break;
  }
  return { art, was, wie: fehler || wie || laeuft, symbol: "box" };
}

/** Zeilen einer einfachen Gegenüberstellung für Edit: alt mit −, neu mit +. */
export function diffZeilen(alt: string, neu: string): { zeichen: "-" | "+"; text: string }[] {
  const a = alt ? alt.split("\n") : [];
  const n = neu ? neu.split("\n") : [];
  // gemeinsamen Anfang und gemeinsames Ende weglassen: man sieht, was sich
  // ändert, nicht die halbe Funktion drumherum
  let vorn = 0;
  while (vorn < a.length && vorn < n.length && a[vorn] === n[vorn]) vorn++;
  let hinten = 0;
  while (hinten < a.length - vorn && hinten < n.length - vorn && a[a.length - 1 - hinten] === n[n.length - 1 - hinten]) hinten++;
  return [
    ...a.slice(vorn, a.length - hinten).map((text) => ({ zeichen: "-" as const, text })),
    ...n.slice(vorn, n.length - hinten).map((text) => ({ zeichen: "+" as const, text })),
  ];
}

/** Sprung aus dem Gedächtnis (docs/gedaechtnis.md, /claude/<id>?bei=<Zeit>):
 *  die Stelle im Verlauf. Die Zeit stammt aus Claude Codes Protokoll und
 *  liegt Sekunden nach der der Brücke -- also die eigene Nachricht, die ihr
 *  am nächsten ist (höchstens 10 min weg), sonst der erste Eintrag ab der
 *  Zeit. -1 = keine Stelle. */
export function sprungStelle(liste: { art: string; zeit?: string }[], bei: string): number {
  const ziel = Date.parse(bei);
  if (!Number.isFinite(ziel)) return -1;
  let best = -1;
  let abstand = Infinity;
  liste.forEach((g, i) => {
    if (g.art !== "du" || !g.zeit) return;
    const d = Math.abs(Date.parse(g.zeit) - ziel);
    if (d < abstand) {
      abstand = d;
      best = i;
    }
  });
  if (best >= 0 && abstand <= 10 * 60_000) return best;
  return liste.findIndex((g) => !!g.zeit && Date.parse(g.zeit) >= ziel);
}
